import React, { useRef, useCallback } from 'react';
import { FileInput, FileField, ImageField, useTranslate } from 'react-admin';
import { Typography, Box } from '@mui/material';
import PropTypes from 'prop-types';
import ImportIcon from '@/assets/icons/import-icon.svg?react';
import ImportLoadingIcon from '@/assets/icons/loading-icon.svg?react';
import { styled } from '@mui/material/styles';
import { importInputBaseStyles } from '@/themes/commonStyles';
import { useWatch } from 'react-hook-form';
import { Palette } from '@/themes/themeOverrides/Palette';
import adsPlaceholder from '@/assets/images/ads-campaign-placeholder.jpg';
import { breakpoints } from '@/themes/themeOverrides/Breakpoints.js';
import { useImagePreview } from '@/composables/useImagePreview';

const StyledCampaignImageInput = styled(Box)({
    ...importInputBaseStyles,
    '& .ra-input-advertisementImage': {
        marginBottom: '0px !important',
    },
    '& .RaLabeled-label': {
        display: 'none',
    },
    '& .RaFileInput-dropZone.RaFileInput-dropZone': {
        position: 'relative',
        display: 'flex',
        gap: '40px',
        padding: '40px',
        minHeight: 'auto',
        borderRadius: '24px',
        marginBottom: '32px',
        [`@media (max-width: ${breakpoints.values.md}px)`]: {
            flexDirection: 'column',
            alignItems: 'flex-start',
            marginBottom: '20px',
        },
        '& + .MuiFormHelperText-root.Mui-error': {
            marginTop: '-30px !important',
            marginBottom: '24px !important',
        },
    },
    '& .RaFileInput-dropZone .import-info.import-info': {
        display: 'flex',
        flexDirection: 'column',
        alignContent: 'center',
        gap: '16px',
        margin: 0,
    },
    '& .RaFileInput-dropZone .import-info .instruction-text': {
        fontSize: '14px',
        lineHeight: '20px',
        letterSpacing: '-0.14px',
        textAlign: 'left',
        margin: 0,
    },
    '& .RaFileInput-dropZone .import-info .import-preview': {
        display: 'block',
        maxWidth: '100%',
        height: 'auto',
        borderRadius: '12px',
        margin: 0,
    },
    '& .RaFileInput-dropZone .import-info .import-preview-ra img': {
        display: 'block',
        maxWidth: '160px',
        maxHeight: '284px',
        height: 'auto',
        borderRadius: '12px',
        margin: 0,
    },
    '& .RaFileInput-dropZone .import-info .btn-import.btn-import': {
        margin: 0,
        '&:hover': {
            backgroundColor: Palette.yellow.yellow400,
            boxShadow: `inset 0 0 0 2px ${Palette.yellow.yellow600}`,
        },
    },
    '& .RaFileInput-dropZone .import-info .btn-import.loaded-margin': {
        margin: '0 auto',
    },
    '& .RaFileInput-dropZone .placeholder-image': {
        width: '160px',
        height: 'auto',
        borderRadius: '12px',
        aspectRatio: '9 / 16',
        objectFit: 'cover',
    },
    '&.has-preview .previews': {
        display: 'none',
    },
    '&.has-preview .RaFileInput-dropZone.RaFileInput-dropZone': {
        gridTemplateColumns: '1fr',
    },
    '&.has-preview .RaFileInput-dropZone .placeholder-image': {
        display: 'none',
    },
    '&.has-preview .RaFileInput-dropZone .import-info .instruction-text': {
        display: 'none',
    },
    '&.has-preview .RaImageField-image': {
        width: '100%',
    },
});

const CampaignImageInput = ({ source = 'advertisementImage', accept = '.jpg,.jpeg,.png', validate, instructionText, loading = false, disabled = false }) => {
    const translate = useTranslate();
    const placeholder = loading ? translate('resources.campaign.labels.verifyingFile') : translate('resources.campaign.labels.chooseFile');

    const value = useWatch({ name: source });
    const previewUrl = useImagePreview(value);
    const showPreview = Boolean(previewUrl) && !loading;
    let containerClassName = '';
    if (showPreview) {
        containerClassName = 'has-preview';
    } else if (loading) {
        containerClassName = 'is-loading';
    }

    const containerRef = useRef(null);
    const handleReupload = useCallback(
        event => {
            event.preventDefault();
            event.stopPropagation();
            if (event.nativeEvent && typeof event.nativeEvent.stopImmediatePropagation === 'function') {
                event.nativeEvent.stopImmediatePropagation();
            }
            const input = containerRef.current?.querySelector('input[type="file"]');
            if (input && !disabled && !loading) {
                input.click();
            }
        },
        [containerRef, disabled, loading],
    );

    return (
        <StyledCampaignImageInput ref={containerRef} className={containerClassName}>
            <FileInput
                source={source}
                accept={accept}
                validate={validate}
                disabled={disabled || loading}
                placeholder={
                    <>
                        <Box className='import-info'>
                            {loading && (
                                <Typography variant='body1' className='btn-import loading' disabled>
                                    <ImportLoadingIcon />
                                    {placeholder}
                                </Typography>
                            )}
                            {!loading && !showPreview && (
                                <>
                                    <Typography className='instruction-text'>{instructionText}</Typography>
                                    <Typography variant='body1' className='btn-import loaded' disabled={disabled}>
                                        <ImportIcon />
                                        {placeholder}
                                    </Typography>
                                </>
                            )}
                            {showPreview && (
                                <>
                                    <ImageField record={{ src: previewUrl }} source='src' className='import-preview-ra' />
                                    <Typography
                                        variant='body1'
                                        className='btn-import loaded loaded-margin'
                                        role='button'
                                        tabIndex={0}
                                        onClick={handleReupload}
                                        onKeyDown={e => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                handleReupload(e);
                                            }
                                        }}
                                    >
                                        <ImportIcon />
                                        {translate('resources.campaign.labels.reupload')}
                                    </Typography>
                                </>
                            )}
                        </Box>
                        {!showPreview && (
                            <img
                                src={adsPlaceholder}
                                alt={translate('resources.campaign.labels.adsPlaceholderAlt')}
                                className='placeholder-image'
                                style={{ opacity: loading ? 0.4 : 1 }}
                            />
                        )}
                    </>
                }
            >
                <FileField source='src' disabled={disabled || loading} />
            </FileInput>
        </StyledCampaignImageInput>
    );
};

CampaignImageInput.propTypes = {
    source: PropTypes.string,
    accept: PropTypes.string,
    validate: PropTypes.func,
    instructionText: PropTypes.string.isRequired,
    loading: PropTypes.bool,
    disabled: PropTypes.bool,
};

export default CampaignImageInput;
